import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../services/api";
import { useToast } from "../components/Toast";
import useConfirm from "../hooks/useConfirm";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import CreditPricingSection from "../components/admin/CreditPricingSection";
import CreditPackagesSection from "../components/admin/CreditPackagesSection";
import UserCreditAdjustment from "../components/admin/UserCreditAdjustment";
import "./AdminCredits.css";

/**
 * Admin page for managing credit pricing, packages, and user adjustments.
 *
 * Role check is handled by ProtectedRoute (requiredRole="admin") in App.js.
 * Server enforces via requireRole("admin") middleware.
 */
const AdminCredits = () => {
  const toast = useToast();
  const { confirmState, confirm } = useConfirm();

  // Credit prices state
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);

  // Credit packages state
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);

  const [error, setError] = useState(null);

  const fetchPrices = useCallback(async () => {
    try {
      const data = await adminApi.getCreditPrices();
      setPrices(data.prices || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 401) {
        setError("Access denied.");
      } else {
        setError(err.response?.data?.message || "Failed to load credit prices");
      }
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  // [B2 FIX] Removed stale `error` closure — was causing dependency on `error` state,
  // which could trigger re-fetches. Use functional updater to avoid overwriting
  // a prices error with a packages error.
  const fetchPackages = useCallback(async () => {
    try {
      const data = await adminApi.getCreditPackages();
      setPackages(data.packages || []);
    } catch (err) {
      setError(
        (prev) =>
          prev ||
          err.response?.data?.message ||
          "Failed to load credit packages",
      );
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    fetchPackages();
  }, [fetchPrices, fetchPackages]);

  const handlePriceUpdate = async (family, creditsPerMessage) => {
    try {
      await adminApi.updateCreditPrice(family, creditsPerMessage);
      toast.success(`${family} credit price updated to ${creditsPerMessage}`);
      await fetchPrices();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update credit price",
      );
    }
  };

  const handlePackageCreate = async (packageData) => {
    try {
      await adminApi.createCreditPackage(packageData);
      toast.success("Credit package created");
      await fetchPackages();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to create credit package",
      );
      throw err;
    }
  };

  const handlePackageUpdate = async (id, packageData) => {
    try {
      await adminApi.updateCreditPackage(id, packageData);
      toast.success("Credit package updated");
      await fetchPackages();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update credit package",
      );
      throw err;
    }
  };

  const handlePackageDelete = async (pkg) => {
    const confirmed = await confirm({
      title: "Deactivate Package",
      message: `Deactivate the "${pkg.name || pkg.modelFamily}" package (${pkg.creditAmount} ${pkg.modelFamily} credits)? It will no longer be available for purchase.`,
      confirmText: "Deactivate",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await adminApi.deleteCreditPackage(pkg.id);
      toast.success("Credit package deactivated");
      await fetchPackages();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to deactivate credit package",
      );
    }
  };

  // [U4 FIX] Reactivation gets the same confirmation treatment as deactivation
  const handlePackageReactivate = async (pkg) => {
    const confirmed = await confirm({
      title: "Reactivate Package",
      message: `Reactivate the "${pkg.name || pkg.modelFamily}" package (${pkg.creditAmount} ${pkg.modelFamily} credits)? It will become available for purchase again.`,
      confirmText: "Reactivate",
    });
    if (!confirmed) return;

    try {
      // Audit #13: include all fields — backend nulls omitted optional fields.
      // (false positive on || undefined: it's correct — preserves non-empty values,
      // and an already-empty name becoming NULL is the same state.)
      await adminApi.updateCreditPackage(pkg.id, {
        modelFamily: pkg.modelFamily,
        creditAmount: pkg.creditAmount,
        priceCents: pkg.priceCents,
        name: pkg.name || undefined,
        stripePriceId: pkg.stripePriceId || undefined,
        sortOrder: pkg.sortOrder ?? 0,
        isActive: true,
      });
      toast.success("Credit package reactivated");
      await fetchPackages();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to reactivate credit package",
      );
    }
  };

  const handleCreditAdjust = async (userId, adjustment) => {
    try {
      // axios interceptor already unwraps { status, data } → data
      const result = await adminApi.adjustUserCredits(userId, adjustment);
      toast.success(
        `${adjustment.amount > 0 ? "Added" : "Deducted"} ${Math.abs(adjustment.amount)} ${adjustment.modelFamily} credits. New balance: ${result.balanceAfter}`,
      );
      return result;
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to adjust user credits",
      );
      throw err;
    }
  };

  if (loadingPrices && loadingPackages) {
    return (
      <div className="admin-credits-page">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="admin-credits-page">
      <div className="admin-credits-header">
        <h2>Credit Management</h2>
        <p className="admin-credits-subtitle">
          Configure credit pricing per model, manage purchasable credit
          packages, and adjust individual user balances.
        </p>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {/* [U6 FIX] Per-section loading — show spinner for each section independently */}
      {loadingPrices ? (
        <div className="admin-credits-section">
          <LoadingSpinner />
        </div>
      ) : (
        <CreditPricingSection prices={prices} onUpdate={handlePriceUpdate} />
      )}

      {loadingPackages ? (
        <div className="admin-credits-section">
          <LoadingSpinner />
        </div>
      ) : (
        <CreditPackagesSection
          packages={packages}
          onCreate={handlePackageCreate}
          onUpdate={handlePackageUpdate}
          onDelete={handlePackageDelete}
          onReactivate={handlePackageReactivate}
        />
      )}

      <UserCreditAdjustment onAdjust={handleCreditAdjust} />

      <ConfirmDialog {...confirmState} />
    </div>
  );
};

export default AdminCredits;
